import {
  useCreateSlide,
  useDeleteSlide,
  useGetPaginateSlide,
} from '../../api-http/slides-v2';
import { SlideResponse } from '../../types/slide.types';
import { DialogModal } from '../../components';
import {
  BulkSelectionBar,
  managementTableSx,
} from '../../components/ManagementPageLayout';
import { AlertBar } from '../../components/AlertBar';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { notifyMsg } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import { RenameSlideForm } from './RenameSlideForm';
import {
  Brush as BrushIcon,
  EditOutlined,
  LockOpenOutlined,
} from '@mui/icons-material';
import {
  Box,
  LinearProgress,
  TablePagination,
  CircularProgress,
  Checkbox,
  IconButton,
  Stack,
} from '@mui/material';
import { Tooltip } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useConfirm } from 'material-ui-confirm';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Copy, Edit3, Trash2 } from 'react-feather';
import { SOCKET_EVENTS } from '../../util/constant';
import useSocket from '../../util/useSocket';
import type { SlideColumns } from './SlidesPage';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

const SlideSizeBadge: React.FC<{ name?: string; ratio?: string }> = ({
  name,
  ratio,
}) => {
  const isPortrait =
    ratio?.includes('9:16') || name?.toLowerCase().includes('vertical');
  const orientation = isPortrait ? 'Portrait' : 'Landscape';
  const displayRatio = ratio || (isPortrait ? '9:16' : '16:9');

  return (
    <Tooltip title={`${name || orientation} (${displayRatio})`}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        aria-label={`${displayRatio} ${orientation}`}
        sx={{
          display: 'inline-flex',
          minWidth: 108,
          px: 1.25,
          py: 0.75,
          bgcolor: '#f1f5ff',
          borderRadius: 2,
          color: '#334155',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: isPortrait ? 11 : 20,
            height: isPortrait ? 19 : 13,
            border: '2px solid #2563eb',
            borderRadius: '3px',
            flexShrink: 0,
          }}
        />
        <Box sx={{ lineHeight: 1.1 }}>
          <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
            {displayRatio}
          </Box>
          <Box
            component="span"
            sx={{ display: 'block', color: '#64748b', fontSize: 12 }}
          >
            {orientation}
          </Box>
        </Box>
      </Stack>
    </Tooltip>
  );
};

interface SlideTableProp {
  searchedSlide?: string;
  selectedDevice?: string;
  onRefresh: boolean;
  visibleColumns: SlideColumns;
}

const SlideTable: React.FC<SlideTableProp> = ({
  searchedSlide,
  selectedDevice,
  onRefresh,
  visibleColumns,
}) => {
  const { socket } = useSocket();
  const isViewOnly = useIsUserRole([UserRole.user, UserRole.dataEditor]);
  const location = useLocation();

  const confirm = useConfirm();
  const { switchedOrgId, currentUser } = useAuth();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable(true);
  const { sortby, order } = pagingParams;

  const {
    data: list,
    isLoading: getAllSlidesIsLoading,
    error,
    refetch,
    isRefetching,
  } = useGetPaginateSlide({
    ...pagingParams,
    device: selectedDevice,
    search: searchedSlide,
  });

  const {
    error: deleteError,
    isLoading: isDeleteLoading,
    mutateAsync: deleteSlide,
  } = useDeleteSlide({
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const { mutate: createSlide, error: createError } = useCreateSlide({
    onSuccess: () => {
      refetch();
      showSuccessSnack(notifyMsg.SLIDE_CLONE_SUCCESS);
    },
    onError: (err) => showErrSnack(err),
  });

  const [renameSlideModal, setRenameSlideModal] = useState(false);
  const [activeSlide, setActiveSlide] = useState<SlideResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    refetch();
    setSelectedIds(new Set());
  }, [switchedOrgId, onRefresh]);

  const delayedEffect = () => {
    refetch();
  };

  useEffect(() => {
    const timeout = setTimeout(delayedEffect, 1000);
    return () => clearTimeout(timeout);
  }, [socket]);

  useEffect(() => {
    refetch();
    setSelectedIds(new Set());
  }, [selectedDevice]);

  useEffect(() => {
    refetch();
    setSelectedIds(new Set());
  }, [searchedSlide]);

  const remove = async (slide: SlideResponse) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      await deleteSlide({ id: slide._id });
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(slide._id);
        return next;
      });
      await refetch();
      showSuccessSnack(notifyMsg.SLIDE_DELETE_SUCCESS);
    } catch (e) {}
  };

  const slides = list?.data || [];
  const pageIds = slides.map((slide) => slide._id);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = pageIds.some((id) => selectedIds.has(id));

  const setSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(pageIds) : new Set());
  };

  const removeSelected = async () => {
    if (!selectedIds.size) return;
    try {
      await confirm({
        title: `Delete ${selectedIds.size} slides?`,
        description:
          'The selected slides will be permanently deleted. This action cannot be undone.',
        confirmationText: 'Delete selected',
      });
      for (const id of Array.from(selectedIds)) {
        await deleteSlide({ id });
      }
      setSelectedIds(new Set());
      await refetch();
      showSuccessSnack('Selected slides deleted');
    } catch (e) {}
  };

  const clone = (slide: SlideResponse) => {
    createSlide({
      dynamicData: slide.dynamicData || [],
      projectData: slide.projectData || {},
      status: slide.status || true,
      title: `${slide.title}_clone`,
    });
  };

  useEffect(() => {
    const { dataConnection, editSlide } = SOCKET_EVENTS;
    if (socket) {
      socket.on(dataConnection, (socketData) => {
        const { event } = socketData;

        if (event === editSlide) {
          refetch();
        }
      });
    }
    return () => {
      if (socket) {
        socket.off(dataConnection);
      }
      refetch();
    };
  }, [socket]);

  const isLinerLoading =
    getAllSlidesIsLoading || isRefetching || isDeleteLoading;

  return (
    <>
      {isLinerLoading && <LinearProgress />}
      <AlertBar
        severity="error"
        msg={error?.msg || deleteError?.msg || createError?.msg}
      />
      <BulkSelectionBar
        count={selectedIds.size}
        singularLabel="slide"
        pluralLabel="slides"
        onClear={() => setSelectedIds(new Set())}
        onDelete={removeSelected}
        disabled={isViewOnly || isDeleteLoading}
      />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(event) => toggleAll(event.target.checked)}
                  disabled={isViewOnly || !pageIds.length}
                  inputProps={{ 'aria-label': 'Select all slides' }}
                />
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.title}
                  direction={sortby === SortEnum.title ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.title)}
                >
                  Slide title
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>Slide size</StyledTableCell>
              <StyledTableCell>Updated by</StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              {visibleColumns.createdBy && (
                <StyledTableCell>Created by</StyledTableCell>
              )}
              {visibleColumns.created && (
                <StyledTableCell>
                  <StyledTableSortLabel
                    active={sortby === SortEnum.createdAt}
                    direction={
                      sortby === SortEnum.createdAt ? order : OrderEnum.asc
                    }
                    onClick={() => sortOnClick(SortEnum.createdAt)}
                  >
                    Created
                  </StyledTableSortLabel>
                </StyledTableCell>
              )}
              <StyledTableCell align="center">Editing</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!slides.length && (
              <TableRow>
                <TableCell
                  colSpan={
                    7 +
                    Number(visibleColumns.createdBy) +
                    Number(visibleColumns.created)
                  }
                  align="center"
                >
                  {isLinerLoading ? <CircularProgress /> : 'No Records Found '}
                </TableCell>
              </TableRow>
            )}
            {slides.map((slide) => {
              const {
                isEditing,
                editingBy,
                _id,
                title,
                updatedBy,
                updatedAt,
                createdAt,
                createdBy,
              } = slide;
              return (
                <TableRow
                  key={_id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(_id)}
                      onChange={(event) =>
                        setSelected(_id, event.target.checked)
                      }
                      disabled={isViewOnly}
                      inputProps={{ 'aria-label': `Select ${title}` }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {isViewOnly || isEditing ? (
                      title
                    ) : (
                      <NavLink
                        style={{
                          textDecoration: 'underline',
                          textUnderlineOffset: '5px',
                        }}
                        to={`/editor-v2/${_id}`}
                        state={{
                          search: location?.search,
                        }}
                      >
                        <BrushIcon
                          sx={{
                            fontSize: '13px',
                            marginRight: '5px',
                            marginBottom: '-2px',
                          }}
                        />
                        {title}
                      </NavLink>
                    )}
                  </TableCell>
                  <TableCell>
                    <SlideSizeBadge
                      name={slide.projectData?.deviceInfo?.name}
                      ratio={slide.projectData?.deviceInfo?.type}
                    />
                  </TableCell>
                  <TableCell>{updatedBy}</TableCell>
                  <TableCell>{displayDate(updatedAt)}</TableCell>
                  {visibleColumns.createdBy && (
                    <TableCell>{createdBy}</TableCell>
                  )}
                  {visibleColumns.created && (
                    <TableCell>{displayDate(createdAt)}</TableCell>
                  )}

                  <TableCell align="center">
                    {isEditing ? (
                      <Tooltip
                        title={
                          editingBy?._id == currentUser?._id
                            ? 'You are editing'
                            : 'Another user is editing'
                        }
                      >
                        <EditOutlined
                          aria-label={
                            editingBy?._id == currentUser?._id
                              ? 'You are editing'
                              : 'Another user is editing'
                          }
                          sx={{
                            color:
                              editingBy?._id == currentUser?._id
                                ? '#2563eb'
                                : '#d97706',
                            fontSize: 22,
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Available for edit">
                        <LockOpenOutlined
                          aria-label="Available for edit"
                          sx={{ color: '#16a34a', fontSize: 22 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Rename slide">
                        <span>
                          <IconButton
                            aria-label={`Rename ${title}`}
                            disabled={isViewOnly}
                            onClick={() => {
                              setActiveSlide(slide);
                              setRenameSlideModal(true);
                            }}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <Edit3 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Duplicate slide">
                        <span>
                          <IconButton
                            aria-label={`Duplicate ${title}`}
                            disabled={isViewOnly}
                            onClick={() => clone(slide)}
                            sx={{ ...actionButtonSx, color: '#64748b' }}
                          >
                            <Copy size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete slide">
                        <span>
                          <IconButton
                            aria-label={`Delete ${title}`}
                            disabled={isViewOnly}
                            onClick={() => remove(slide)}
                            sx={{
                              ...actionButtonSx,
                              color: '#dc2626',
                              bgcolor: '#fffafa',
                              borderColor: '#fecaca',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>

      <DialogModal
        title="Rename slide"
        onClose={() => setRenameSlideModal(false)}
        open={renameSlideModal}
      >
        <RenameSlideForm
          onFinished={() => {
            setRenameSlideModal(false);
            setActiveSlide(null);
            refetch();
          }}
          slide={activeSlide}
        />
      </DialogModal>
    </>
  );
};

export default SlideTable;
